import type { ComponentType } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Link } from '@tanstack/react-router';
import {
  NavBarchartIcon,
  NavHourglassIcon,
  NavListpadIcon,
  NavClockIcon,
  NavQuestionIcon,
} from '@ui/icons';
import { cn } from '@shared/lib';
import { t } from '@shared/i18n';
import RilomaLogo from '@shared/assets/Images/riloma_Logo.svg?react';
import WatermarkSVG from '@shared/assets/Images/watermark.svg?react';

type IconType = ComponentType<{ className?: string }>;

interface NavItem {
  label: string;
  icon: IconType;
  /** Route to navigate to. */
  to?:
    | '/'
    | '/needs-attention'
    | '/pending-settlements'
    | '/recent-activity'
    | '/audit-trail'
    | '/help-support';
}

const NAV_ITEMS: NavItem[] = [
  { label: t.shell.nav.home, icon: NavBarchartIcon, to: '/' },
  { label: t.shell.nav.pendingSettlements, icon: NavHourglassIcon, to: '/pending-settlements' },
  { label: t.shell.nav.recentActivities, icon: NavListpadIcon, to: '/recent-activity' },
  { label: t.shell.nav.auditTrail, icon: NavClockIcon, to: '/audit-trail' },
  { label: t.shell.nav.help, icon: NavQuestionIcon, to: '/help-support' },
];

const itemClass =
  'flex h-15 w-full items-center gap-3 pl-6 pr-4 text-base font-light text-fg transition-colors hover:bg-surface focus-visible:bg-surface focus-visible:outline-none';

export interface SideMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Left navigation drawer (Figma "Side Menu"), shared across all internal pages — opened from the
 * header hamburger. Wordmark header + nav links + a faint brand watermark. Built on Radix Dialog
 * (focus-trapped, Esc/backdrop to close). Routeless items are presentational until their pages
 * exist; wire `to` as routes land.
 */
export function SideMenu({ open, onOpenChange }: SideMenuProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed inset-y-0 left-0 z-50 flex w-68.25 max-w-[85vw] flex-col overflow-hidden bg-surface-raised shadow-overlay outline-none"
        >
          <Dialog.Title className="sr-only">{t.shell.nav.label}</Dialog.Title>

          <div className="flex h-20.5 shrink-0 items-center border-b border-border bg-header-bar px-7">
            <RilomaLogo aria-hidden className="h-6.75 w-auto" />
          </div>

          <nav aria-label={t.shell.nav.label} className="relative flex flex-1 flex-col pt-2">
            {NAV_ITEMS.map(({ label, icon: Icon, to }) =>
              to ? (
                <Link
                  key={label}
                  to={to}
                  activeOptions={{ exact: true }}
                  onClick={() => onOpenChange(false)}
                  className={itemClass}
                  activeProps={{
                    className: cn(itemClass, 'bg-accent-subtle font-normal text-accent-text'),
                  }}
                >
                  <Icon className="size-7 shrink-0" />
                  {label}
                </Link>
              ) : (
                <button
                  key={label}
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className={itemClass}
                >
                  <Icon className="size-7 shrink-0" />
                  {label}
                </button>
              ),
            )}

            {/* <RilomaMark
              aria-hidden
              className="pointer-events-none absolute -bottom-6 -left-5 z-0 size-44 rotate-14 opacity-[0.04]"
            /> */}
            <WatermarkSVG className="absolute bottom-0 left-0 pointer-events-none" />
          </nav>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
